import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Icon from 'common/components/elements/icons';
import { SpinnerLoader } from 'common/components-v2/elements/loader';
import GenerateColor from 'common/utils/generateTextColor.js';

const Button = ({
   text,
   size = 'large',
   buttonType = 'button',
   leftIconName,
   rightIconName,
   loading,
   onClick,
   disabled,
   type = 'primary',
   isDistructive,
   className = '',
   buttonClassName = '',
   primaryColor,
   textClassName,
   ...rest
}) => {
   const [isFocused, setIsFocused] = useState(false);

   const IS_PRIMARY_BUTTON = !disabled && 'primary' === type && !isDistructive;
   const IS_LINK_BUTTON_SPINNER = 'link' === type && !isDistructive;
   const IS_OUTLINED_BUTTON_SPINNER = 'outlined' === type && !isDistructive;

   const getPrimaryBackground = (bool, opacity) => {
      if(primaryColor && bool){
         return {
            backgroundColor: opacity ? GenerateColor.generateRGBColor(primaryColor, opacity) : primaryColor,
         }
      }
      return {}
   }
   const getPrimaryText = (bool, opacity) => {
      if(primaryColor && bool){
         return {
            color: opacity ? GenerateColor.generateRGBColor(primaryColor, opacity) : primaryColor,
         }
      }
      return {}
   }

   return (
      <div
         className={ cx({
            [`flex items-center justify-center ${ className }`]: true,
         }) }
      >
         <button
            onFocus={ () => setIsFocused(true) }
            onBlur={ () => setIsFocused(false) }
            style={ {
               transition: 'all 0.5s ease',
               boxShadow: isFocused ?  '0px 0px 0px 4px #DEDDFF' : '0px 0px 0px 0px #ffff',
               ...getPrimaryBackground(IS_PRIMARY_BUTTON),
            } }
            disabled={ disabled || loading }
            onClick={ e => {
               if(onClick){
                  onClick(e)
               }
               setIsFocused(true)
               setTimeout(() => {
                  setIsFocused(false)
               }, 500)
            } }
            type={ buttonType }
            className={ cx({
               [`flex items-center justify-center   outline-none relative overflow-hidden group ${ buttonClassName }`]: true,
               'h-13 gap-[10px] min-w-[52px]': size === 'large',
               'h-10 gap-2 min-w-[40px]': size === 'medium',
               'h-9 gap-[6px] min-w-[36px]': size === 'small',
               'px-6': size === 'large' && text,
               'px-4': size === 'medium' && text,
               'px-[14px]': size === 'small' && text,
               'bg-gray-300 dark:bg-gray-dark-300': disabled && 'primary' === type,
               'bg-blue dark:bg-blue-dark': IS_PRIMARY_BUTTON && !primaryColor,
               'group/primary-dark': IS_PRIMARY_BUTTON && primaryColor && GenerateColor.isDarkMode(primaryColor),
               'bg-red dark:bg-red-dark': !disabled && 'primary' === type && isDistructive,
               'bg-gray-100 dark:bg-gray-dark-100': 'secondary' === type,
               'bg-gray-200 dark:bg-gray-dark-200': 'pill' === type,
               'border border-border-100 dark:border-border-dark-100': 'outlined' === type,
               'rounded-full': 'pill' === type,
               'rounded-label': 'pill' !== type,
            }) }
            { ...rest }
         >
            {
               loading && !disabled && (
                  <SpinnerLoader
                     style={ {
                        ...getPrimaryBackground(IS_LINK_BUTTON_SPINNER),
                     } }
                     size={ size }
                     bgColor={ cx({
                        'bg-panel': 'primary' === type,
                        'bg-gray-600 dark:bg-gray-dark-600': ['tertiary', 'outlined', 'secondary', 'pill'].includes(type),
                        'bg-blue dark:bg-blue-dark': IS_LINK_BUTTON_SPINNER && !primaryColor,
                        'bg-red dark:bg-red-dark': 'link' === type && isDistructive,
                     }) }
                  />
               )
            }

            {
               !disabled && (
                  <div
                     style={ {
                        transition: 'all 0.5s ease',
                        ...getPrimaryBackground(!isFocused && IS_LINK_BUTTON_SPINNER, 0.8),
                     } }
                     className={ cx({
                        'bg-transparent  absolute top-0 left-0 z-1 w-full h-full pointer-events-none hidden group-hover:flex': true,
                        'group-hover:bg-black-6 dark:group-hover:bg-white-6': 'primary' === type && !isFocused,
                        'group-hover:bg-black-4 dark:group-hover:bg-white-4': ['secondary', 'pill', 'tertiary', 'outlined'].includes(type) || !isFocused,
                        'group-hover:bg-blue-600-6 dark:group-hover:bg-blue-dark-600-6': !isFocused && IS_LINK_BUTTON_SPINNER && !primaryColor,
                        'group-hover:bg-red-600-6 dark:group-hover:bg-red-dark-600-6': !isFocused && 'link' === type && isDistructive,
                     }) }
                  />
               )
            }
            {
               leftIconName && (
                  <ButtonIcon
                     name={ leftIconName }
                     size={ size }
                     type={ type }
                     disabled={ disabled }
                     loading={ loading }
                     isDistructive={ isDistructive }
                     getPrimaryText={ getPrimaryText }
                     primaryColor={ primaryColor }
                  />
               )
            }
            {
               text && (
                  <span
                     style={ {
                        ...getPrimaryText((IS_LINK_BUTTON_SPINNER || IS_OUTLINED_BUTTON_SPINNER) && !disabled),
                     } }
                     className={ cx({
                        'font-medium select-none': true,
                        'text-lg leading-[20px]': size === 'large',
                        'text-base leading-[18px]': size === 'medium',
                        'text-sm leading-[18px]': size === 'small',
                        'text-major group-odd/primary-dark:text-major-dark': 'primary' === type && primaryColor,
                        'text-major-dark': 'primary' === type && !primaryColor,
                        'text-gray dark:text-gray-dark': ['secondary', 'pill'].includes(type) && !disabled,
                        'text-gray-300 dark:text-gray-dark-300': ['secondary', 'tertiary', 'outlined', 'link', 'pill'].includes(type) && disabled,
                        'text-gray-600 dark:text-gray-dark-600': ['tertiary', 'outlined'].includes(type) && !disabled,
                        'text-blue dark:text-blue-dark': (IS_LINK_BUTTON_SPINNER || IS_OUTLINED_BUTTON_SPINNER) && !disabled && !primaryColor,
                        'text-red dark:text-red-dark': 'link' === type && !disabled && isDistructive,
                        'invisible': loading,
                        [`${ textClassName }`]: textClassName,
                     }) }
                     dangerouslySetInnerHTML={ { __html: text } }
                  />
               )
            }
            {
               rightIconName && (
                  <ButtonIcon
                     name={ rightIconName }
                     size={ size }
                     type={ type }
                     disabled={ disabled }
                     loading={ loading }
                     isDistructive={ isDistructive }
                     getPrimaryText={ getPrimaryText }
                     primaryColor={ primaryColor }
                  />
               )
            }
         </button>
      </div>
   );
};

const ButtonIcon = ({
   size,
   name,
   type,
   disabled,
   loading,
   isDistructive,
   getPrimaryText,
   primaryColor,
}) => {
   return (
      <div
         className={ cx({
            'flex items-center justify-center': true,
            'h-6 w-6 min-w-[24px]': size === 'large',
            'h-5 w-5 min-w-[20px]': size === 'medium',
            'h-4 w-4 min-w-[16px]': size === 'small',
            'invisible': loading,
         }) }
      >
         <Icon
            style={ {
               ...getPrimaryText(['link', 'outlined'].includes(type) && !disabled && !isDistructive),
            } }
            className={ cx({
               'text-major group-odd/primary-dark:text-major-dark': 'primary' === type && primaryColor,
               'text-major-dark': 'primary' === type && !primaryColor,
               'text-blue dark:text-blue-dark': 'link' === type && !disabled && !isDistructive && !primaryColor,
               'text-red dark:text-red-dark': 'link' === type && !disabled && isDistructive,
               'text-gray-600 dark:text-gray-dark-600': ['secondary', 'tertiary', 'outlined', 'pill'].includes(type) && !disabled,
               'text-gray-300 dark:text-gray-dark-300': ['secondary', 'tertiary', 'outlined', 'link', 'pill'].includes(type) && disabled && !primaryColor,
               'text-xl': size === 'large',
               'text-base': size === 'medium',
               'text-sm': size === 'small',
            }) }
            size={ null }
            color={ null }
            name={ name }
         />
      </div>
   )
}

ButtonIcon.propTypes = {
   loading: PropTypes.bool,
   name: PropTypes.string,
   disabled: PropTypes.bool,
   isDistructive: PropTypes.bool,
   getPrimaryText: PropTypes.func,
   primaryColor: PropTypes.string,
   size: PropTypes.oneOf([
      'large', 'medium', 'small',
   ]),
   type: PropTypes.oneOf([
      'primary', 'secondary', 'tertiary', 'link', 'outlined', 'pill',
   ]),
};
Button.propTypes = {
   text: PropTypes.string,
   onClick: PropTypes.func,
   leftIconName: PropTypes.string,
   rightIconName: PropTypes.string,
   primaryColor: PropTypes.string,
   className: PropTypes.string,
   buttonClassName: PropTypes.string,
   loading: PropTypes.bool,
   disabled: PropTypes.bool,
   isDistructive: PropTypes.bool,
   size: PropTypes.oneOf([
      'large', 'medium', 'small',
   ]),
   buttonType: PropTypes.oneOf([
      'button', 'submit', 'reset',
   ]),
   type: PropTypes.oneOf([
      'primary', 'secondary', 'tertiary', 'link', 'outlined', 'pill',
   ]),
   textClassName: PropTypes.string,
};

export default Button;
